import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Github, Linkedin, ExternalLink } from 'lucide-react';
import PublicContactSection from '../shared/public-contact-section';
import { SkillProjectsPopup, useSkillProjects } from '../../components/skill-projects-popup';
import {
  TemplateWrapper, HeroSection, Avatar, Name, Bio,
  LinksRow, LinkItem, Section, SectionTitle, EntryCard, EntryHeader,
  EntryTitle, EntrySubtitle, EntryDate, EntryDescription, SkillsGrid,
  SkillBadge, ProjectsGrid, ProjectCard, ProjectImage, ProjectImagePlaceholder,
  ProjectBody, ProjectTitle, ProjectDescription, ProjectLinks, ProjectLink,
  CertLink, ContactSectionWrapper,
} from './classic-template.styles';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function DateRange({ start, end }) {
  const startLabel = formatDate(start);
  const endLabel = formatDate(end);
  if (!startLabel && !endLabel) return null;
  return <EntryDate>{startLabel || '—'} — {endLabel || (end ? '—' : 'Present')}</EntryDate>;
}

function ProfileAvatar({ profile }) {
  const picture = typeof profile?.picture === 'string' ? profile.picture.trim() : '';
  const [isVisible, setIsVisible] = useState(Boolean(picture));

  if (!isVisible) return null;
  return (
    <Avatar
      src={picture}
      alt={profile.profile_name || profile.username || 'Profile'}
      onError={() => setIsVisible(false)}
    />
  );
}

export default function ClassicTemplate({ data }) {
  const navigate = useNavigate();
  const { selectedSkill, isPopupVisible, handleSkillClick, handleClosePopup } = useSkillProjects();

  if (!data) return null;
  const profile = data.profile || {};
  const educations = Array.isArray(data.educations) ? data.educations.filter(Boolean) : [];
  const experiences = Array.isArray(data.experiences) ? data.experiences.filter(Boolean) : [];
  const projects = Array.isArray(data.projects) ? data.projects.filter(Boolean) : [];
  const certifications = Array.isArray(data.certifications) ? data.certifications.filter(Boolean) : [];
  const skills = Array.isArray(data.skills) ? data.skills.filter((skill) => skill?.name) : [];
  const organisations = Array.isArray(data.organisations) ? data.organisations.filter(Boolean) : [];

  const getKey = (item, index) => item?.id || `${item?.name || item?.title || 'entry'}-${index}`;

  return (
    <TemplateWrapper>
      {/* Hero */}
      <HeroSection>
        <ProfileAvatar profile={profile} />
        {(profile.profile_name || profile.username) && <Name>{profile.profile_name || profile.username}</Name>}
        {profile.bio && <Bio>{profile.bio}</Bio>}
        {(profile.email || profile.github || profile.linkedin) && <LinksRow>
          {profile?.email && (
            <LinkItem href={`mailto:${profile.email}`}><Mail size={16} /> {profile.email}</LinkItem>
          )}
          {profile?.github && (
            <LinkItem href={profile.github} target="_blank" rel="noreferrer"><Github size={16} /> GitHub</LinkItem>
          )}
          {profile?.linkedin && (
            <LinkItem href={profile.linkedin} target="_blank" rel="noreferrer"><Linkedin size={16} /> LinkedIn</LinkItem>
          )}
        </LinksRow>}
      </HeroSection>

      {/* Skills */}
      {skills.length > 0 && (
        <Section>
          <SectionTitle>Skills</SectionTitle>
          <SkillsGrid>
            {skills.map((skill, index) => (
              <SkillBadge
                key={getKey(skill, index)}
                type="button"
                onClick={() => handleSkillClick(skill)}
              >
                {skill.name}
              </SkillBadge>
            ))}
          </SkillsGrid>
        </Section>
      )}

      {/* Experience */}
      {experiences.length > 0 && (
        <Section>
          <SectionTitle>Experience</SectionTitle>
          {experiences.map((exp, index) => (
            <EntryCard key={getKey(exp, index)}>
              <EntryHeader>
                <div>
                  {(exp.role || exp.name) && <EntryTitle>{exp.role || exp.name}</EntryTitle>}
                  {(exp.company || exp.location) && <EntrySubtitle>{exp.company}{exp.location ? ` · ${exp.location}` : ''}</EntrySubtitle>}
                </div>
                <DateRange start={exp.start_date} end={exp.end_date} />
              </EntryHeader>
              {exp.description && <EntryDescription>{exp.description}</EntryDescription>}
            </EntryCard>
          ))}
        </Section>
      )}

      {/* Education */}
      {educations.length > 0 && (
        <Section>
          <SectionTitle>Education</SectionTitle>
          {educations.map((edu, index) => (
            <EntryCard key={getKey(edu, index)}>
              <EntryHeader>
                <div>
                  <EntryTitle>{edu.degree || edu.name}{edu.field_of_study ? ` in ${edu.field_of_study}` : ''}</EntryTitle>
                  {edu.university && <EntrySubtitle>{edu.university}</EntrySubtitle>}
                </div>
                <DateRange start={edu.start_date} end={edu.end_date} />
              </EntryHeader>
              {edu.description && <EntryDescription>{edu.description}</EntryDescription>}
            </EntryCard>
          ))}
        </Section>
      )}

      {/* Projects */}
      {projects.length > 0 && (
        <Section>
          <SectionTitle>Projects</SectionTitle>
          <ProjectsGrid>
            {projects.map((project, index) => (
              <ProjectCard
                key={getKey(project, index)}
                onClick={() => project.id && navigate(`/projects/${project.id}`)}
              >
                {project.cover_image_url ? (
                  <ProjectImage src={project.cover_image_url} alt={project.title || 'Project'} />
                ) : (
                  <ProjectImagePlaceholder>{project.title}</ProjectImagePlaceholder>
                )}
                <ProjectBody>
                  {project.title && <ProjectTitle>{project.title}</ProjectTitle>}
                  {project.description && (
                    <ProjectDescription>
                      {project.description.length > 150 ? project.description.substring(0, 150) + '...' : project.description}
                    </ProjectDescription>
                  )}
                  {(project.github_url || project.live_url) && <ProjectLinks>
                    {project.github_url && (
                      <ProjectLink href={project.github_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                        <Github size={14} /> Code
                      </ProjectLink>
                    )}
                    {project.live_url && (
                      <ProjectLink href={project.live_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                        <ExternalLink size={14} /> Live
                      </ProjectLink>
                    )}
                  </ProjectLinks>}
                </ProjectBody>
              </ProjectCard>
            ))}
          </ProjectsGrid>
        </Section>
      )}

      {/* Certifications */}
      {certifications.length > 0 && (
        <Section>
          <SectionTitle>Certifications</SectionTitle>
          {certifications.map((cert, index) => (
            <EntryCard key={getKey(cert, index)}>
              <EntryHeader>
                <div>
                  <EntryTitle>{cert.title || cert.name}</EntryTitle>
                  {cert.issuer && <EntrySubtitle>{cert.issuer}</EntrySubtitle>}
                </div>
                {cert.end_date && <EntryDate>{formatDate(cert.end_date)}</EntryDate>}
              </EntryHeader>
              {cert.credential_url && (
                <CertLink href={cert.credential_url} target="_blank" rel="noreferrer">
                  View Credential →
                </CertLink>
              )}
            </EntryCard>
          ))}
        </Section>
      )}

      {/* Organisations */}
      {organisations.length > 0 && (
        <Section>
          <SectionTitle>Organisations</SectionTitle>
          {organisations.map((org, index) => (
            <EntryCard key={getKey(org, index)}>
              <EntryHeader>
                <div>
                  <EntryTitle>{org.name}</EntryTitle>
                  {org.role && <EntrySubtitle>{org.role}</EntrySubtitle>}
                </div>
                <DateRange start={org.start_date} end={org.end_date} />
              </EntryHeader>
              {org.description && <EntryDescription>{org.description}</EntryDescription>}
            </EntryCard>
          ))}
        </Section>
      )}

      {/* Contact */}
      <ContactSectionWrapper>
        <PublicContactSection 
          profile={profile} 
          palette={{
            surface: 'transparent',
            card: '#fffdf9',
            input: '#f5f4ef',
            border: '#deded5',
            text: '#20201d',
            muted: '#6f7069',
            iconBg: '#fffdf9',
            iconFg: '#b65d3b',
            buttonBg: '#b65d3b',
            buttonText: '#ffffff',
          }}
        />
      </ContactSectionWrapper>
      <SkillProjectsPopup
        visible={isPopupVisible}
        onHide={handleClosePopup}
        skill={selectedSkill}
      />
    </TemplateWrapper>
  );
}
